// frontend/src/components/integrations/CanonicalPropertiesPage.js
import React, { useEffect, useState } from 'react';
import {
  Table, Button, Tag, Space, Input, Alert, message, Popconfirm, Modal, Form, Select, Drawer, Divider,
} from 'antd';
import { PlusOutlined, LinkOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import useCanonicalProperties from './hooks/useCanonicalProperties';
import SuggestLinksModal from './SuggestLinksModal';
import apiClient from '../../utils/axios';

const { Search } = Input;

// FastAPI 422 validation errors return detail as an array of objects, not a string.
function extractErrorMessage(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join('; ');
  return fallback;
}

const TYPE_COLORS = { hubspot: '#e8440a', esputnik: '#0070cc', stripe: '#5433c5' };
const STRATEGY_LABELS = { primary: 'primary', latest: 'latest', first_set: 'first set' };
const STRATEGY_OPTIONS = [
  { value: 'latest', label: 'Latest — most recent value wins' },
  { value: 'primary', label: 'Primary — lowest priority number wins' },
  { value: 'first_set', label: 'First Set — keep original, never overwrite' },
];

function TypeBadge({ typeId }) {
  const color = TYPE_COLORS[typeId] || '#888';
  return (
    <span style={{
      display: 'inline-block',
      width: 8, height: 8, borderRadius: '50%',
      background: color, marginRight: 6,
    }} />
  );
}

function LinkChip({ link, onRemove }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
      borderRadius: 6, padding: '2px 8px', fontSize: 11, marginBottom: 3,
    }}>
      <TypeBadge typeId={link.type_id} />
      <span style={{ color: 'var(--text-secondary)' }}>{link.type_id}</span>
      <span style={{ fontFamily: 'monospace', color: 'var(--text-primary)', fontWeight: 500 }}>{link.ext_key}</span>
      <span style={{
        background: 'var(--bg-tertiary)', borderRadius: 3, padding: '0 4px',
        color: 'var(--text-muted)', fontSize: 10,
      }}>{STRATEGY_LABELS[link.merge_strategy] || link.merge_strategy}</span>
      {onRemove && (
        <DeleteOutlined
          style={{ color: 'var(--text-muted)', cursor: 'pointer', fontSize: 10 }}
          onClick={onRemove}
        />
      )}
    </span>
  );
}

export default function CanonicalPropertiesPage() {
  const {
    definitions, loading, error,
    suggestions, suggestionsLoading,
    load, createProperty, updateProperty, deleteProperty,
    upsertLink, removeLink,
    fetchSuggestions, applyApprovedSuggestions,
  } = useCanonicalProperties();

  const [search, setSearch] = useState('');
  const [showSuggest, setShowSuggest] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm] = Form.useForm();

  // Edit property (label / data_type)
  const [editingProp, setEditingProp] = useState(null);
  const [editPropForm] = Form.useForm();
  const [editPropSaving, setEditPropSaving] = useState(false);

  // All integration fields — loaded once for the field picker
  const [allFields, setAllFields] = useState([]); // { integration_id, type_id, integration_name, ext_key, label }
  const [fieldsLoading, setFieldsLoading] = useState(false);

  // Link editor drawer state
  const [editingDef, setEditingDef] = useState(null); // the canonical property being edited
  const [linkForm] = Form.useForm();
  const [linkSaving, setLinkSaving] = useState(false);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setFieldsLoading(true);
    apiClient.get('/integrations/properties/all-fields')
      .then(res => setAllFields(res.data.fields || []))
      .catch(() => {}) // non-fatal — link form will show empty picker
      .finally(() => setFieldsLoading(false));
  }, []);

  // ── Suggest / apply ──────────────────────────────────────────────────────
  const handleSuggestOpen = async () => {
    setShowSuggest(true);
    await fetchSuggestions();
  };

  const handleApply = async (accepted) => {
    const count = await applyApprovedSuggestions(accepted);
    message.success(`Applied ${count} link${count !== 1 ? 's' : ''}`);
    setShowSuggest(false);
  };

  // ── Create property ───────────────────────────────────────────────────────
  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields();
      await createProperty(values);
      message.success(`Property '${values.key}' created`);
      setShowCreate(false);
      createForm.resetFields();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(extractErrorMessage(err, 'Failed to create property'));
    }
  };

  // ── Delete property ───────────────────────────────────────────────────────
  const handleDelete = async (key) => {
    try {
      await deleteProperty(key);
      message.success(`Property '${key}' deleted`);
    } catch (err) {
      message.error(extractErrorMessage(err, 'Failed to delete property'));
    }
  };

  // ── Edit property metadata ────────────────────────────────────────────────
  const openEditProp = (row) => {
    setEditingProp(row);
    editPropForm.setFieldsValue({
      key: row.key,
      label: row.label,
      data_type: row.data_type,
    });
  };

  const handleEditPropSave = async () => {
    try {
      const values = await editPropForm.validateFields();
      setEditPropSaving(true);
      const oldKey = editingProp.key;
      const keyChanged = !editingProp.is_default && values.key !== oldKey;

      if (keyChanged) {
        // Delete old + recreate with new key, preserving links
        await deleteProperty(oldKey);
        await createProperty({
          key: values.key,
          label: values.label,
          data_type: values.data_type,
        });
        // Re-attach all existing links under the new key
        for (const [intId, link] of Object.entries(editingProp.links || {})) {
          await upsertLink(values.key, intId, {
            type_id: link.type_id,
            ext_key: link.ext_key,
            merge_strategy: link.merge_strategy,
            priority: link.priority,
          });
        }
      } else {
        await updateProperty(oldKey, { label: values.label, data_type: values.data_type });
      }

      message.success(`Property updated`);
      setEditingProp(null);
      editPropForm.resetFields();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(extractErrorMessage(err, 'Failed to update property'));
    } finally {
      setEditPropSaving(false);
    }
  };

  // ── Link editor ───────────────────────────────────────────────────────────
  const [editingLinkIntId, setEditingLinkIntId] = useState(null); // integration_id being edited, null = new

  const openLinkEditor = (row) => {
    setEditingDef(row);
    setEditingLinkIntId(null);
    linkForm.resetFields();
    linkForm.setFieldsValue({ merge_strategy: 'latest', priority: 1 });
  };

  const closeLinkEditor = () => {
    setEditingDef(null);
    setEditingLinkIntId(null);
    linkForm.resetFields();
  };

  const startEditLink = (intId, link) => {
    setEditingLinkIntId(intId);
    linkForm.setFieldsValue({
      integration_id: intId,
      type_id: link.type_id,
      ext_key: link.ext_key,
      field_display_key: `${intId}__${link.ext_key}`,
      merge_strategy: link.merge_strategy || 'latest',
      priority: link.priority ?? 1,
    });
  };

  const cancelEditLink = () => {
    setEditingLinkIntId(null);
    linkForm.resetFields();
    linkForm.setFieldsValue({ merge_strategy: 'latest', priority: 1 });
  };

  const handleSaveLink = async () => {
    try {
      const values = await linkForm.validateFields();
      setLinkSaving(true);
      // If integration changed during edit, remove the old link first
      if (editingLinkIntId && values.integration_id !== editingLinkIntId) {
        await removeLink(editingDef.key, editingLinkIntId);
      }
      await upsertLink(editingDef.key, values.integration_id, {
        type_id: values.type_id,
        ext_key: values.ext_key,
        merge_strategy: values.merge_strategy || 'latest',
        priority: Number(values.priority) || 1,
      });
      setEditingLinkIntId(null);
      linkForm.resetFields();
      linkForm.setFieldsValue({ merge_strategy: 'latest', priority: 1 });
      message.success(editingLinkIntId ? 'Link updated' : 'Link added');
    } catch (err) {
      if (err?.errorFields) return;
      message.error(extractErrorMessage(err, 'Failed to save link'));
    } finally {
      setLinkSaving(false);
    }
  };

  const handleRemoveLink = async (key, integrationId) => {
    try {
      await removeLink(key, integrationId);
      message.success('Link removed');
    } catch (err) {
      message.error(extractErrorMessage(err, 'Failed to remove link'));
    }
  };

  // Keep editingDef in sync after load() refreshes definitions.
  // Intentionally listing only [definitions] — adding editingDef would cause an infinite loop.
  // eslint-disable-next-line
  useEffect(() => {
    if (editingDef) {
      const refreshed = definitions.find(d => d.key === editingDef.key);
      if (refreshed) setEditingDef(refreshed);
    }
  }, [definitions]); // eslint-disable-line

  // ── Table ─────────────────────────────────────────────────────────────────
  const filtered = definitions.filter(d => {
    if (!search) return true;
    const q = search.toLowerCase();
    return d.key.toLowerCase().includes(q) || d.label.toLowerCase().includes(q);
  });

  const columns = [
    {
      title: 'Label / Key',
      key: 'label',
      width: '22%',
      render: (_, row) => (
        <div>
          <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{row.label}</span>
          {row.is_default && (
            <Tag style={{ marginLeft: 6, fontSize: 10, padding: '0 5px' }}>default</Tag>
          )}
          <br />
          <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--color-primary)' }}>{row.key}</span>
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'data_type',
      key: 'data_type',
      width: '10%',
      render: (v) => <Tag>{v}</Tag>,
    },
    {
      title: 'Linked Integration Fields',
      key: 'links',
      render: (_, row) => {
        const linkEntries = Object.entries(row.links || {});
        if (linkEntries.length === 0) {
          return <span style={{ color: 'var(--text-muted)', fontSize: 11, fontStyle: 'italic' }}>No links — not tracked</span>;
        }
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {linkEntries.map(([intId, link]) => (
              <LinkChip key={intId} link={link} />
            ))}
          </div>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: '22%',
      render: (_, row) => (
        <Space size={4}>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEditProp(row)}
          >
            Edit
          </Button>
          <Button
            size="small"
            icon={<LinkOutlined />}
            onClick={() => openLinkEditor(row)}
          >
            Links
          </Button>
          {!row.is_default && (
            <Popconfirm
              title={`Delete property '${row.key}'?`}
              onConfirm={() => handleDelete(row.key)}
              okText="Delete"
              okButtonProps={{ danger: true }}
            >
              <Button size="small" danger type="text">Delete</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{
      background: 'var(--bg-primary)',
      borderRadius: 12,
      border: '1px solid var(--border-color)',
      padding: '1.5rem',
      gridColumn: '1 / -1',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: 600 }}>
            Contact Properties
          </h3>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-secondary)' }}>
            Canonical properties linked to integration fields
          </p>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <Search
            placeholder="Search properties…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: 220 }}
            allowClear
          />
          <Button onClick={handleSuggestOpen}>
            ✦ Suggest Links
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setShowCreate(true)}
          >
            Add Property
          </Button>
        </div>
      </div>

      {error && <Alert type="error" message={error} style={{ marginBottom: 12 }} />}

      <Table
        loading={loading}
        dataSource={filtered}
        columns={columns}
        rowKey="key"
        size="small"
        pagination={false}
        scroll={{ y: 480 }}
      />

      <p style={{ marginTop: 10, fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>
        Only properties with at least one linked integration field will generate timeline events on change.
      </p>

      {/* ── Edit Property Modal ───────────────────────────────────────── */}
      <Modal
        open={!!editingProp}
        title="Edit Property"
        onOk={handleEditPropSave}
        confirmLoading={editPropSaving}
        onCancel={() => { setEditingProp(null); editPropForm.resetFields(); }}
        okText="Save"
      >
        <Form form={editPropForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="key"
            label="Key"
            rules={[
              { required: true, message: 'Key is required' },
              { pattern: /^[a-z][a-z0-9_]*$/, message: 'Lowercase letters, numbers and underscores only' },
            ]}
            tooltip={editingProp?.is_default ? 'Default property keys cannot be changed' : undefined}
          >
            <Input
              placeholder="e.g. mrr"
              disabled={editingProp?.is_default}
              style={editingProp?.is_default ? { fontFamily: 'monospace' } : undefined}
            />
          </Form.Item>
          <Form.Item name="label" label="Label" rules={[{ required: true, message: 'Label is required' }]}>
            <Input placeholder="e.g. Monthly Recurring Revenue" />
          </Form.Item>
          <Form.Item name="data_type" label="Type">
            <Select options={[
              { value: 'string', label: 'String' },
              { value: 'number', label: 'Number' },
              { value: 'boolean', label: 'Boolean' },
              { value: 'date', label: 'Date' },
            ]} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Suggest Links Modal ───────────────────────────────────────── */}
      <SuggestLinksModal
        open={showSuggest}
        suggestions={suggestions}
        loading={suggestionsLoading}
        onApply={handleApply}
        onCancel={() => setShowSuggest(false)}
      />

      {/* ── Create Property Modal ─────────────────────────────────────── */}
      <Modal
        open={showCreate}
        title="Add Custom Property"
        onOk={handleCreate}
        onCancel={() => { setShowCreate(false); createForm.resetFields(); }}
        okText="Create"
      >
        <Form form={createForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="key"
            label="Key"
            rules={[
              { required: true, message: 'Key is required' },
              { pattern: /^[a-z][a-z0-9_]*$/, message: 'Lowercase letters, numbers and underscores only' },
            ]}
          >
            <Input placeholder="e.g. mrr" />
          </Form.Item>
          <Form.Item name="label" label="Label" rules={[{ required: true }]}>
            <Input placeholder="e.g. MRR" />
          </Form.Item>
          <Form.Item name="data_type" label="Type" initialValue="string">
            <Select options={[
              { value: 'string', label: 'String' },
              { value: 'number', label: 'Number' },
              { value: 'boolean', label: 'Boolean' },
              { value: 'date', label: 'Date' },
            ]} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Link Editor Drawer ────────────────────────────────────────── */}
      <Drawer
        open={!!editingDef}
        onClose={closeLinkEditor}
        title={editingDef ? `Links for "${editingDef.label}"` : 'Edit Links'}
        width={480}
      >
        {editingDef && (
          <>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 0 }}>
              Integration fields mapped to{' '}
              <code style={{ fontFamily: 'monospace', color: 'var(--color-primary)' }}>{editingDef.key}</code>.
              The merge strategy determines which value wins when multiple integrations report conflicting data.
            </p>

            {/* Current links */}
            {Object.keys(editingDef.links || {}).length === 0 ? (
              <p style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: 13 }}>
                No integration fields linked yet.
              </p>
            ) : (
              <div style={{ marginBottom: 16 }}>
                {Object.entries(editingDef.links).map(([intId, link]) => {
                  const isBeingEdited = editingLinkIntId === intId;
                  return (
                    <div key={intId} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '8px 12px',
                      border: `1px solid ${isBeingEdited ? 'var(--color-primary)' : 'var(--border-color)'}`,
                      borderRadius: 8, marginBottom: 6,
                      background: isBeingEdited ? '#f0f5ff' : 'var(--bg-secondary)',
                    }}>
                      <div>
                        <Space size={6}>
                          <TypeBadge typeId={link.type_id} />
                          <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{link.type_id}</span>
                          <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)' }}>
                            {link.ext_key}
                          </span>
                        </Space>
                        <br />
                        <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 14 }}>
                          {STRATEGY_LABELS[link.merge_strategy] || link.merge_strategy}
                          {link.merge_strategy === 'primary' && ` · priority ${link.priority}`}
                        </span>
                      </div>
                      <Space size={4}>
                        <Button
                          size="small"
                          type={isBeingEdited ? 'primary' : 'default'}
                          onClick={() => isBeingEdited ? cancelEditLink() : startEditLink(intId, link)}
                        >
                          {isBeingEdited ? 'Cancel' : 'Edit'}
                        </Button>
                        <Popconfirm
                          title="Remove this link?"
                          onConfirm={() => handleRemoveLink(editingDef.key, intId)}
                          okText="Remove"
                          okButtonProps={{ danger: true }}
                        >
                          <Button size="small" danger type="text" icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </Space>
                    </div>
                  );
                })}
              </div>
            )}

            <Divider />

            <h4 style={{ color: 'var(--text-primary)', marginTop: 0 }}>
              {editingLinkIntId ? 'Edit link' : 'Add a link'}
            </h4>
            <Form form={linkForm} layout="vertical">
              {/* Hidden fields — populated when a field option is selected */}
              <Form.Item name="integration_id" hidden><Input /></Form.Item>
              <Form.Item name="type_id" hidden><Input /></Form.Item>
              <Form.Item name="ext_key" hidden><Input /></Form.Item>

              <Form.Item
                name="field_display_key"
                label="Integration field"
                rules={[{ required: true, message: 'Select a field from an integration' }]}
              >
                <Select
                  showSearch
                  placeholder={fieldsLoading ? 'Loading fields…' : 'Search and select a field…'}
                  loading={fieldsLoading}
                  filterOption={(input, option) =>
                    (option?.searchText || '').toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={(value) => {
                    const field = allFields.find(
                      f => `${f.integration_id}__${f.ext_key}` === value
                    );
                    if (field) {
                      linkForm.setFieldsValue({
                        integration_id: field.integration_id,
                        type_id: field.type_id,
                        ext_key: field.ext_key,
                      });
                    }
                  }}
                  notFoundContent={fieldsLoading ? 'Loading…' : 'No fields found'}
                  options={allFields.map(f => ({
                    value: `${f.integration_id}__${f.ext_key}`,
                    searchText: `${f.integration_name} ${f.label} ${f.ext_key} ${f.type_id}`,
                    label: (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <TypeBadge typeId={f.type_id} />
                        <span style={{ fontWeight: 500 }}>{f.label}</span>
                        <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-muted)' }}>
                          {f.ext_key}
                        </span>
                        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>
                          {f.integration_name}
                        </span>
                      </div>
                    ),
                  }))}
                />
              </Form.Item>

              <Form.Item name="merge_strategy" label="Merge strategy">
                <Select options={STRATEGY_OPTIONS} />
              </Form.Item>
              <Form.Item
                noStyle
                shouldUpdate={(prev, cur) => prev.merge_strategy !== cur.merge_strategy}
              >
                {({ getFieldValue }) =>
                  getFieldValue('merge_strategy') === 'primary' ? (
                    <Form.Item name="priority" label="Priority (lower wins)">
                      <Input type="number" min={1} />
                    </Form.Item>
                  ) : null
                }
              </Form.Item>
              <Form.Item style={{ marginBottom: 0 }}>
                <Button type="primary" onClick={handleSaveLink} loading={linkSaving} block>
                  {editingLinkIntId ? 'Save changes' : 'Add link'}
                </Button>
              </Form.Item>
            </Form>
          </>
        )}
      </Drawer>
    </div>
  );
}
